import { useEffect, useRef, useState } from 'react';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { loginRequest } from './config/authConfig';

/* Pages */
import HomePage from './pages/HomePage';
import ConfigurationPage from './pages/ConfigurationPage';
import TaskDetailPage from './pages/TaskDetailPage';
import TaskActualsPage from './pages/TaskActualsPage';
import ProjectActualsPage from './pages/ProjectActualsPage';
import UserDetailPage from './pages/UserDetailPage';
import ActivityHourForecastPage from './pages/ActivityHourForecastPage';
import TaskOdcForecastPage from './pages/TaskOdcForecastPage';
import ChartsPage from './pages/ChartsPage';
import ProjectEditPage from './pages/ProjectEditPage';
import MyWorkplanPage from './pages/MyWorkplanPage';
import ProjectCreateWizardPage from './pages/ProjectCreateWizardPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import TaskOrderDetailPage from './pages/TaskOrderDetailPage';
import TaskOrderInvoicesPage from './pages/TaskOrderInvoicesPage';
import WbsManagerPage from './pages/WbsManagerPage';
import EmployeeConnectCloseDatesSamplePage from './pages/EmployeeConnectCloseDatesSamplePage';

/* Components */
import LoadingSpinner from './components/LoadingSpinner';
import Sidebar from './components/navigation/Sidebar';
import BottomNav from './components/navigation/BottomNav';

import './App.css';

function AuthWrapper({ children }) {
    const { instance, accounts, inProgress } = useMsal();
    const redirectAttemptedRef = useRef(false);

    useEffect(() => {
        if (inProgress !== 'none') return;

        const active = instance.getActiveAccount();
        if (!active && accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
        }

        if (accounts.length === 0 && !redirectAttemptedRef.current) {
            redirectAttemptedRef.current = true;
            instance.loginRedirect(loginRequest).catch((error) => {
                console.error('Login redirect failed:', error);
            });
        }
    }, [instance, accounts, inProgress]);

    if (inProgress !== 'none') {
        return (
            <LoadingSpinner
                message="Loading..."
                description="Please wait while we initialize the application."
            />
        );
    }

    if (accounts.length === 0) {
        return (
            <LoadingSpinner
                message="Redirecting to login..."
                description="Please wait while we redirect you to sign in."
            />
        );
    }

    return children;
}

function App({ instance }) {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

    return (
        <div className="App">
            <MsalProvider instance={instance}>
                <Router>
                    <AuthWrapper>
                        <div
                            className="app-shell"
                            data-sidebar-collapsed={sidebarCollapsed ? 'true' : 'false'}
                        >
                            <Sidebar
                                collapsed={sidebarCollapsed}
                                onToggle={() => setSidebarCollapsed((v) => !v)}
                            />

                            <div className="main-content">
                                <div className="p-3">
                                    <Routes>
                                        <Route path="/" element={<HomePage />} />
                                        <Route path="/projects/new" element={<ProjectCreateWizardPage />} />
                                        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
                                        <Route path="/projects/:projectId/task-orders/:taskOrderId" element={<TaskOrderDetailPage />} />
                                        <Route path="/projects/:projectId/task-orders/:taskOrderId/invoices" element={<TaskOrderInvoicesPage />} />
                                        <Route path="/projects/:projectId/task-orders/:taskOrderId/tasks/:taskId" element={<TaskDetailPage />} />
                                        <Route path="/projects/:projectId/task-orders/:taskOrderId/tasks/:taskId/actuals" element={<TaskActualsPage />} />
                                        <Route path="/projects/:projectId/task-orders/:taskOrderId/actuals" element={<ProjectActualsPage />} />
                                        <Route path="/projects/:projectId/task-orders/:taskOrderId/tasks/:taskId/activities/:activityId/forecast" element={<ActivityHourForecastPage />} />
                                        <Route path="/projects/:projectId/task-orders/:taskOrderId/tasks/:taskId/odc-forecast" element={<TaskOdcForecastPage />} />
                                        <Route path="/projects/:projectId/edit" element={<ProjectEditPage />} />
                                        <Route path="/projects/:projectId/actuals" element={<ProjectActualsPage />} />
                                        <Route path="/my-workplan" element={<MyWorkplanPage />} />
                                        <Route path="/charts" element={<ChartsPage />} />
                                        <Route path="/wbs-manager" element={<WbsManagerPage />} />
                                        <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
                                        <Route path="/tasks/:taskId/actuals" element={<TaskActualsPage />} />
                                        <Route path="/tasks/:taskId/activities/:activityId/forecast" element={<ActivityHourForecastPage />} />
                                        <Route path="/tasks/:taskId/odc-forecast" element={<TaskOdcForecastPage />} />
                                        <Route path="/configuration" element={<ConfigurationPage />} />
                                        <Route path="/connect-samples/employeeconnect-close-dates" element={<EmployeeConnectCloseDatesSamplePage />} />
                                        <Route path="/users/:userId" element={<UserDetailPage />} />
                                        <Route path="*" element={<Navigate to="/" replace />} />
                                    </Routes>
                                </div>
                            </div>
                        </div>

                        <BottomNav />
                    </AuthWrapper>
                </Router>
            </MsalProvider>
        </div>
    );
}

export default App;
